import { Injectable, Inject } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AuthClientService } from '@rsm/client/auth-client';
import { SecurityService, config, IConfig } from './security.service';
import { User } from './credential';

@Injectable({ providedIn: 'root' })
export class SecurityGuard implements CanActivate {
  constructor(
    private router: Router,
    private securityService: SecurityService,
    private authClientService: AuthClientService,
    @Inject(config) private config: IConfig
  ) {}

  canActivate(next: ActivatedRouteSnapshot): Observable<boolean> | boolean {
    if (!this.securityService.isValid()) {
      this.router.navigate([this.config.loginRoute]);
      return false;
    }

    const { expiredPassword } = this.securityService.credential;

    if (expiredPassword) {
      this.router.navigate([this.config.passwordRoute]);
      return false;
    }

    // Agente sem vínculo de gestão: autenticado, mas o painel não é para ele.
    // Vai direto ao cadastro da própria empresa — único caminho que o token
    // restrito abre — em vez de cair numa tela de seleção de empresa vazia.
    if (!this.securityService.hasPanelAccess) {
      this.router.navigate([this.config.contextRoute]);
      return false;
    }

    const roles = next.data['security']?.roles || [];

    if (!this.securityService.hasRoles(roles)) {
      this.router.navigate([this.config.rootRoute]);
      return false;
    }

    if (this.securityService.credential.user?.contextId) {
      return true;
    }

    return this.authClientService.findAllContexts().pipe(
      switchMap((contexts) => {
        if (contexts?.length === 1) {
          return this.selectContext(contexts[0]);
        }

        this.router.navigate([this.config.contextRoute]);
        return of(false);
      }),
      catchError(() => {
        this.router.navigate([this.config.contextRoute]);
        return of(false);
      })
    );
  }

  private selectContext(context: any): Observable<boolean> {
    const { userContextId } = context;
    const { refreshToken } = this.securityService.credential;

    return this.authClientService.refresh({ refreshToken, userContextId }).pipe(
      map((data) => {
        this.securityService.init(this.mapToUser(data));
        return true;
      }),
      catchError(() => {
        this.router.navigate([this.config.contextRoute]);
        return of(false);
      })
    );
  }

  private mapToUser(data: any): User {
    return {
      id: data.userId,
      name: data.name,
      login: data.login,
      email: data.email,
      roles: data.roles,
      profile: data.profile,
      context: data.context,
      contextId: data.contextId,
      isRoot: data.isRoot,
      accessToken: data.accessToken,
      expiresIn: data.accessExpiresIn,
      refreshToken: data.refreshToken,
      userContextId: data.userContextId,
      expiredPassword: data.temporaryPassword,
      panelAccess: data.panelAccess,
    };
  }
}
